import { useQuery } from "@apollo/client/react";
import { HiOutlineDocumentText } from "react-icons/hi";
import {
  ProblematicWordsDocument,
  VocabularyGrowthDocument,
  WordsMasteredDocument,
} from "../../../generated/graphql";
import type { FilterState } from "../../../pages/fsrs/AnalyticsDashboardPage";
import { VocabularyGrowthChart } from "../charts/VocabularyGrowthChart";
import { WordsTable } from "../charts/WordsTable";

interface WordsTabProps {
  filters: FilterState;
}

export function WordsTab({ filters }: WordsTabProps) {
  const { selectedStudentId, selectedLessonId } = filters;

  const { data: masteredData, loading: masteredLoading } = useQuery(WordsMasteredDocument, {
    variables: {
      studentId: selectedStudentId || "",
      lessonId: selectedLessonId || null,
      limit: 50,
      offset: 0,
    },
    skip: !selectedStudentId,
  });

  const { data: growthData, loading: growthLoading } = useQuery(VocabularyGrowthDocument, {
    variables: {
      studentId: selectedStudentId || "",
      dateRange: null,
    },
    skip: !selectedStudentId,
  });

  const { data: problematicData, loading: problematicLoading } = useQuery(
    ProblematicWordsDocument,
    {
      variables: {
        studentId: selectedStudentId || "",
        lessonId: selectedLessonId || null,
        threshold: 0.3,
        limit: 20,
      },
      skip: !selectedStudentId,
    }
  );

  // Empty state if no student selected
  if (!selectedStudentId) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <HiOutlineDocumentText className="text-6xl mb-4 mx-auto text-gray-600" />
          <h3 className="h5 mb-2">Select a Student</h3>
          <p className="text-gray-600">
            Choose a student from the filters above to view word analysis
          </p>
        </div>
      </div>
    );
  }

  const isLoading = masteredLoading || growthLoading || problematicLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-600">Loading word analysis...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="h4">Word/Vocabulary Analysis</h2>
        <p className="text-gray-600 mt-1">
          Track vocabulary growth, mastery progress, and identify problematic words
        </p>
      </div>

      {/* Section 1: Vocabulary Growth Chart */}
      <section>
        <h3 className="h5 mb-4">Vocabulary Growth Over Time</h3>
        <div className="panel p-6">
          <VocabularyGrowthChart data={growthData?.vocabularyGrowth || []} />
        </div>
      </section>

      {/* Section 2: Words Mastered Table */}
      <section>
        <h3 className="h5 mb-4">Words Mastered</h3>
        <WordsTable
          words={masteredData?.wordsMastered.words || []}
          total={masteredData?.wordsMastered.total || 0}
        />
      </section>

      {/* Section 3: Problematic Words */}
      {problematicData?.problematicWords && problematicData.problematicWords.length > 0 && (
        <section>
          <h3 className="h5 mb-4">Problematic Words</h3>
          <div className="panel p-4 border-l-4 border-l-warning-500">
            <p className="caption mb-4 text-warning-400">
              These words have high lapse rates and need extra attention
            </p>
            <div className="space-y-3">
              {problematicData.problematicWords.map((word) => (
                <div key={word.slideId} className="panel p-4 border-l-2 border-l-error-500">
                  <div className="flex items-center justify-between mb-2">
                    <span className="h6 text-teal-400">{word.word}</span>
                    <span className="badge-danger">
                      {(word.lapseRate * 100).toFixed(0)}% lapse rate
                    </span>
                  </div>
                  <div className="caption mb-2">
                    {word.lapseCount} lapses • Difficulty: {word.currentDifficulty.toFixed(1)}
                  </div>
                  <div className="caption bg-primary-900/30 border border-primary-700 rounded p-2 text-primary-400">
                    {word.recommendation}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
